import { css } from '@emotion/react';
import { RadioButtonRow, space } from 'folio-common-components';
import * as React from 'react';
import { Box } from '../../components/box';
import { Button } from '../../components/buttons';
import { type RootState, useActions, useSelector } from '../../state';
import {
  type Props as DetailsFormProps,
  OrgTransferOrgDetailsForm,
} from './org-transfer-org-details-form';

function selector(state: RootState) {
  const signatoryId = state.editor.coreInfo.orgTransfer?.signatory;

  const signatory =
    signatoryId && signatoryId !== ''
      ? state.editor.people[signatoryId]
      : undefined;

  return {
    orgTransfer: state.editor.coreInfo.orgTransfer,
    signatory,
  };
}

export const OrgTransferForm: React.FC = () => {
  const { orgTransfer, signatory } = useSelector(selector);
  const { editorActions } = useActions();

  // tracks if the editor should be visible
  const [showEditor, setShowEditor] = React.useState(false);

  const onToggleIsTransfer = (isTransfer: boolean) => {
    if (isTransfer) {
      editorActions.setCoreInfo({
        orgTransfer: {
          orgId: '',
          orgName: '',
          signatory: '',
        },
      });
    } else {
      editorActions.clearOrgTransfer();
      setShowEditor(false);
    }
  };

  const onSave: DetailsFormProps['onSave'] = data => {
    setShowEditor(false);
    editorActions.setOrgTransfer({
      orgId: data.org.orgId,
      orgName: data.org.orgName,
      signatory: data.signer,
    });
  };

  const hasCompany =
    orgTransfer && orgTransfer.orgId !== '' && orgTransfer.orgName !== '';

  type ContentKind =
    | 'pre-form' // Show the "legg inn bedriften" button and prose
    | 'form' // Show the form
    | 'complete'; // Show summary for a completed form

  let contentKind: ContentKind = 'pre-form';

  if (showEditor) {
    contentKind = 'form';
  } else if (hasCompany) {
    contentKind = 'complete';
  }

  let content: React.ReactNode = null;

  switch (contentKind) {
    case 'pre-form': {
      content = (
        <div>
          <p>
            Vi kommer til å trenge signaturen til eieren av den eksisterende
            bedriften. Obs! Hvis du skal overta noe delvis, kan du ikke
            registrere det her.
          </p>
          <Button
            id="show-org-transfer-form"
            level="secondary"
            onClick={() => setShowEditor(true)}
          >
            Legg inn bedriften
          </Button>
        </div>
      );
      break;
    }

    case 'form': {
      content = (
        <OrgTransferOrgDetailsForm
          onCancel={() => setShowEditor(false)}
          onSave={onSave}
        />
      );
      break;
    }

    case 'complete': {
      content = (
        <Box css={space([16], 'margin-top')}>
          {/* FIXME: same as PersonRoleItemName */}
          <div
            css={css`
              font-size: 1.2em;
              font-weight: 600;
            `}
          >
            {orgTransfer?.orgName}
          </div>
          <div>Ved {signatory ? signatory.name : 'ukjent'}</div>
          <Button
            level="secondary"
            onClick={() => {
              setShowEditor(true);
            }}
            css={space([16], 'margin-top')}
          >
            Endre
          </Button>
        </Box>
      );
    }
  }

  return (
    <>
      <p>
        Overtar selskapet virk&shy;somheten i et eksisterende foretak? Svaret
        har ingen betydning praktisk eller skatte&shy;messig, men
        Brønnøy&shy;sund&shy;registrene bruker det&nbsp;i&nbsp;statistikk.
      </p>
      <RadioButtonRow
        inputProps={{
          checked: orgTransfer == null,
          onChange: () => onToggleIsTransfer(false),
          name: 'orgTransfer',
        }}
        id="orgTransferFalse"
        label="Nei, jeg starter noe nytt"
      />

      <RadioButtonRow
        inputProps={{
          checked: orgTransfer != null,
          onChange: () => onToggleIsTransfer(true),
          name: 'orgTransfer',
        }}
        id="orgTransferTrue"
        label="Ja, selskapet skal ta over aktiviteten fra en eksisterende bedrift"
      />

      {orgTransfer ? content : null}
    </>
  );
};
